import React from "react";
import { Calendar } from "lucide-react";
import PropTypes from "prop-types";
import dayjs from "dayjs";

const QuickDateRange = ({
  selectedRange,
  onRangeChange,
  showFinancialYears = true,
}) => {
  const handleRangeChange = range => {
    const today = dayjs();
    let fromDate, toDate;

    switch (range) {
      case "lastWeek":
        fromDate = today.subtract(1, "week").startOf("week");
        toDate = today.subtract(1, "week").endOf("week");
        break;
      case "lastMonth":
        fromDate = today.subtract(1, "month").startOf("month");
        toDate = today.subtract(1, "month").endOf("month");
        break;
      case "thisMonth":
        fromDate = today.startOf("month");
        toDate = today;
        break;
      case "lastQuarter":
        // Calculate last quarter
        const currentMonth = today.month();
        const currentQuarter = Math.floor(currentMonth / 3);
        const lastQuarter = currentQuarter === 0 ? 3 : currentQuarter - 1;
        const lastQuarterYear =
          currentQuarter === 0 ? today.year() - 1 : today.year();

        fromDate = dayjs()
          .year(lastQuarterYear)
          .month(lastQuarter * 3)
          .startOf("month");
        toDate = dayjs()
          .year(lastQuarterYear)
          .month(lastQuarter * 3 + 2)
          .endOf("month");
        break;
      case "lastFinancialYear":
        // Financial year in India is April to March
        const currentYear = today.year();
        const isAfterMarch = today.month() > 2; // April is 3 (zero-based)

        if (isAfterMarch) {
          // Last financial year is previous year April to current year March
          fromDate = dayjs()
            .year(currentYear - 1)
            .month(3)
            .startOf("month"); // April 1
          toDate = dayjs().year(currentYear).month(2).endOf("month"); // March 31
        } else {
          // Last financial year is 2 years ago April to last year March
          fromDate = dayjs()
            .year(currentYear - 2)
            .month(3)
            .startOf("month"); // April 1
          toDate = dayjs()
            .year(currentYear - 1)
            .month(2)
            .endOf("month"); // March 31
        }
        break;
      case "currentFinancialYear":
        // Current financial year
        const currYear = today.year();
        const isAfterMarchCurrent = today.month() > 2;

        if (isAfterMarchCurrent) {
          // Current FY is current year April to next year March
          fromDate = dayjs().year(currYear).month(3).startOf("month");
          toDate = dayjs()
            .year(currYear + 1)
            .month(2)
            .endOf("month");
        } else {
          // Current FY is previous year April to current year March
          fromDate = dayjs()
            .year(currYear - 1)
            .month(3)
            .startOf("month");
          toDate = dayjs().year(currYear).month(2).endOf("month");
        }
        break;
      case "custom":
      default:
        // Let the parent component handle custom dates
        onRangeChange(range, null, null);
        return;
    }

    onRangeChange(range, fromDate, toDate);
  };

  return (
    <div className="mt-6">
      <div className="flex items-center gap-2 mb-3">
        <Calendar size={16} />
        <span className="text-sm font-medium text-gray-600">
          Quick Date Ranges
        </span>
      </div>
      <div className="flex flex-wrap gap-2 mb-6 min-h-[38px]">
        <button
          type="button"
          onClick={() => handleRangeChange("lastWeek")}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            selectedRange === "lastWeek"
              ? "bg-[#EAD8FC] text-[#5652B0] border-2 border-[#5652B0]"
              : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-[#F5EDFD] hover:text-[#5652B0] hover:border-[#5652B0]"
          } transition-all min-w-[90px] h-[38px]`}>
          Last Week
        </button>

        <button
          type="button"
          onClick={() => handleRangeChange("lastMonth")}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            selectedRange === "lastMonth"
              ? "bg-[#EAD8FC] text-[#5652B0] border-2 border-[#5652B0]"
              : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-[#F5EDFD] hover:text-[#5652B0] hover:border-[#5652B0]"
          } transition-all min-w-[100px] h-[38px]`}>
          Last Month
        </button>

        <button
          type="button"
          onClick={() => handleRangeChange("thisMonth")}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            selectedRange === "thisMonth"
              ? "bg-[#EAD8FC] text-[#5652B0] border-2 border-[#5652B0]"
              : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-[#F5EDFD] hover:text-[#5652B0] hover:border-[#5652B0]"
          } transition-all min-w-[100px] h-[38px]`}>
          This Month
        </button>

        <button
          type="button"
          onClick={() => handleRangeChange("lastQuarter")}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            selectedRange === "lastQuarter"
              ? "bg-[#EAD8FC] text-[#5652B0] border-2 border-[#5652B0]"
              : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-[#F5EDFD] hover:text-[#5652B0] hover:border-[#5652B0]"
          } transition-all min-w-[110px] h-[38px]`}>
          Last Quarter
        </button>

        {showFinancialYears && (
          <>
            <button
              type="button"
              onClick={() => handleRangeChange("lastFinancialYear")}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                selectedRange === "lastFinancialYear"
                  ? "bg-[#EAD8FC] text-[#5652B0] border-2 border-[#5652B0]"
                  : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-[#F5EDFD] hover:text-[#5652B0] hover:border-[#5652B0]"
              } transition-all min-w-[150px] h-[38px]`}>
              Last Financial Year
            </button>

            <button
              type="button"
              onClick={() => handleRangeChange("currentFinancialYear")}
              className={`px-4 py-2 rounded-lg text-sm font-medium ${
                selectedRange === "currentFinancialYear"
                  ? "bg-[#EAD8FC] text-[#5652B0] border-2 border-[#5652B0]"
                  : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-[#F5EDFD] hover:text-[#5652B0] hover:border-[#5652B0]"
              } transition-all min-w-[170px] h-[38px]`}>
              Current Financial Year
            </button>
          </>
        )}

        <button
          type="button"
          onClick={() => handleRangeChange("custom")}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            selectedRange === "custom"
              ? "bg-[#EAD8FC] text-[#5652B0] border-2 border-[#5652B0]"
              : "bg-gray-100 text-gray-700 border border-gray-200 hover:bg-[#F5EDFD] hover:text-[#5652B0] hover:border-[#5652B0]"
          } transition-all min-w-[80px] h-[38px]`}>
          Custom
        </button>
      </div>
    </div>
  );
};

QuickDateRange.propTypes = {
  selectedRange: PropTypes.string.isRequired,
  onRangeChange: PropTypes.func.isRequired,
  showFinancialYears: PropTypes.bool,
};

export default QuickDateRange;
